import { Sentry, initSentry, setSentryUser, clearSentryUser } from '@thistle/mainframe-sentry';
import { authService } from '@thistle/mainframe-auth';
import '@thistle/mainframe-auth/vanillaAuthOverlay';
import { ENV } from './config/environment.js';

// Initialize the application
async function initApp() {
  // Initialize Sentry early
  initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    appName: 'production-planning-frontend',
  });

  try {
    // Configure and initialize authentication
    authService.configure(ENV.mainframeTokenIssuerUrl);
    await authService.initializeAuth();

    // Listen for auth events
    document.addEventListener('thistle-signed-in', () => {
      console.log('User signed in');
      const user = authService.getUser();
      if (user) {
        setSentryUser({
          username: user.name,
          email: user.email,
          identityProvider: user.identityProvider,
        });
      }
      renderApp();
    });

    document.addEventListener('thistle-signed-out', () => {
      console.log('User signed out');
      clearSentryUser();
      renderApp();
    });

    // Initial render
    renderApp();

    console.log('Production Planning application initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Production Planning application:', error);
    Sentry.captureException(error);

    // Show error message to user
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background-color: #fef2f2;">
          <div style="text-align: center;">
            <h1 style="font-size: 1.5rem; font-weight: bold; color: #7f1d1d; margin-bottom: 0.5rem;">Application Error</h1>
            <p style="color: #b91c1c; margin-bottom: 1rem;">Failed to load the application. Please refresh the page.</p>
            <button
              onclick="location.reload()"
              style="background-color: #dc2626; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.25rem; cursor: pointer;"
            >
              Refresh Page
            </button>
          </div>
        </div>
      `;
    }
  }
}

function renderApp() {
  const app = document.getElementById('app');
  if (!app) return;

  if (authService.isAuthenticated()) {
    const user = authService.getUser();
    app.innerHTML = `
      <div style="min-height: 100vh; padding: 2rem; font-family: system-ui, sans-serif;">
        <h1 style="font-size: 2rem; font-weight: bold; margin-bottom: 1rem;">Production Planning</h1>
        <p style="margin-bottom: 1rem;">Welcome, ${user?.name || 'User'}!</p>
        <button
          id="signout-btn"
          style="background-color: #dc2626; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.25rem; cursor: pointer;"
        >
          Sign Out
        </button>
      </div>
    `;

    document.getElementById('signout-btn')?.addEventListener('click', () => {
      authService.signOut();
    });
  } else {
    app.innerHTML = `
      <div style="min-height: 100vh; padding: 2rem; font-family: system-ui, sans-serif;">
        <h1 style="font-size: 2rem; font-weight: bold; margin-bottom: 1rem;">Production Planning</h1>
        <p>Please sign in to continue.</p>
        <auth-overlay data-google-client-id="${ENV.googleClientId}"></auth-overlay>
      </div>
    `;
  }
}

// Start the app when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
